import { QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";
import { App } from "./App";
import { GamificationProvider } from "./features/gamification/GamificationContext";
import { AuthProvider } from "./lib/shared/auth";
import { initCrossSiteAttribution } from "./lib/shared/crossSiteAttribution";
import { migrateStorageKeys } from "./lib/shared/migrate-storage-keys";
import { PostHogBridge } from "./lib/shared/posthog-bridge";
import { registerServiceWorker } from "./lib/shared/push-notifications";
import { queryClient } from "./lib/shared/query-client";
import { initSentry } from "./lib/shared/sentry";
import { ThemeProvider } from "./lib/shared/theme";
import "./index.css";

migrateStorageKeys();
initSentry();

// Stamp outbound links to sibling DataViking properties with cross-site UTMs
// (separate PostHog projects can't stitch sessions natively). See module docs.
initCrossSiteAttribution();

// Register service worker for push notifications (non-blocking)
if ("serviceWorker" in navigator) {
  registerServiceWorker();
}

const isDev = import.meta.env.DEV;

const app = (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <PostHogBridge />
          <GamificationProvider>
            <App />
          </GamificationProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element #root not found");
}
createRoot(rootElement).render(isDev ? <StrictMode>{app}</StrictMode> : app);
